import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { YoutubeVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/youtube-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { MobileFrameComponent } from '../../../preview-common/mobile-frame/mobile-frame.component';

/**
 * YouTube Video Ad on a Google video partner site — the placement that runs
 * the same video creative outside YouTube. The publisher page is a generic
 * article list; the video takes over the top of it as a dismissable panel with
 * the player in the middle. It takes any video format: the creative is
 * contained on a dark player, so a 1:1 or 9:16 upload is pillarboxed.
 */
@Component({
    selector: 'app-youtube-video-partners-in-stream-mobile',
    imports: [CommonModule, MobileFrameComponent],
    templateUrl: './video-partners-in-stream-mobile.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './video-partners-in-stream-mobile.component.scss'
})
export class YoutubeVideoPartnersInStreamMobilePreviewComponent {
  @Input() data!: YoutubeVideoPreviewData;

  /** Skeleton article rows under the panel; the last one clips at the frame. */
  readonly articles = [1, 2, 3, 4];

  private get videoId(): string {
    return this.data.video?.videoId ?? '';
  }

  /** Thumbnail of the video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailFor(this.videoId);
  }

  /**
   * True while the video is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolving(this.videoId);
  }

  /** The full path, which is what this panel shows of the destination. */
  get displayUrl(): string {
    const path = (this.data.url ?? '')
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/$/, '');
    return path || UrlDisplayHelper.PLACEHOLDER_HOST;
  }

  get headline(): string {
    return this.data.headline || 'Your headline appears here';
  }

  get ctaText(): string {
    return this.data.ctaText || 'Your call to action';
  }
}
